import React, { useContext, useEffect, useState } from "react";
import { defineMessages, FormattedMessage, injectIntl, intlShape } from "react-intl";
import { InstallContext } from "./InstallContext";
import useRequest from "../../reactHooks/useRequest";
import classNames from "classnames";
import { Button, Link, Title } from "@yoast/ui-library";
import { Link as RouterLink } from "react-router-dom";
import * as styles from "./InstallStyles.scss";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/20/solid";
import { StepNavigation } from "./StepNavigation";

const messages = defineMessages( {
	title: {
		id: "downloading.title",
		defaultMessage: "You're almost done!",
	},
	downloadStartedNotice: {
		id: "downloading.downloadStartedNotice",
		defaultMessage: "Your download has started! Didn't see a download? {startAgainLink}.",
	},
	startAgain: {
		id: "downloading.startAgain",
		defaultMessage: "Click here to download again",
	},
	uploadInstruction: {
		id: "downloading.uploadInstruction",
		defaultMessage: "Once your download is finished, upload the plugin to your site and activate it to start using {plugin}.",
		values: { plugin: "Yoast SEO Premium" },
	},
	goToSite: {
		id: "downloading.goToSite",
		defaultMessage: "Go to my site to upload the plugin",
	},
	note: {
		id: "downloading.note",
		defaultMessage: "Note: ",
	},
	otherPluginDirections: {
		id: "downloading.otherPluginDirections",
		defaultMessage: "You're downloading {plugin}. Looking for other plugins? Find them in {yourMyYoastAccountLink}",
		values: {
			plugin: "Yoast SEO Premium",
			yourMyYoastAccountLink: <RouterLink to={ "/downloads" }>your MyYoast account.</RouterLink>,
		},
	},
	videoDescription: {
		id: "downloading.videoDescription",
		defaultMessage: "Need help? Watch the short step-by-step video to help you install and activate {plugin}.",
		values: { plugin: "Yoast SEO Premium" },
	},
} );


/**
 * Renders the downloading component. Which shows instructions about installing Yoast plugins.
 *
 * @returns {React.JSX.Element} The rendered component.
 */
const Downloading = ( { intl } ) => {
	const {
		      premiumDownloadUrl,
		      connectedSiteURL,
		      invoiceNumberParam,
		      downloadHasStarted,
		      markDownloadStarted,
	      } = useContext( InstallContext );

	const [ showDownloadIframe ]  = useState( downloadHasStarted === false );
	const url                     = invoiceNumberParam ? `Orders/productGroups/${ invoiceNumberParam }` : "";
	const { data: productGroups } = useRequest( url, "GET", {} );

	const plugins = productGroups.filter( productGroup => productGroup.parent && productGroup.parent.slug === "all-plugins" );

	useEffect( () => {
		if ( showDownloadIframe ) {
			markDownloadStarted();
		}
	}, [] );

	/**
	 * Redirects the user to the upload plugin zip page on the selected site.
	 *
	 * @returns {void}
	 */
	function goToSite() {
		const customerSite = connectedSiteURL.replace( /\/$/, "" );
		window.open( customerSite + "/wp-admin/plugin-install.php?tab=upload" );
	}

	return (
		<div className={ classNames( styles.downloadingStep, styles.ySpacing ) }>
			<Title className={ styles.textCenter }><FormattedMessage { ...messages.title } /></Title>
			<p className={ styles.textCenter }>
				<FormattedMessage
					tagName="em"
					{ ...messages.downloadStartedNotice }
					values={ {
						startAgainLink: <Link href={ premiumDownloadUrl } target="_blank">
							{ intl.formatMessage( messages.startAgain ) }
						</Link>,
					} }
				/>
			</p>
			<hr />

			<p><FormattedMessage { ...messages.uploadInstruction } /></p>

			{ connectedSiteURL && <div>
				<Button
					id="install-addon-redirect-action"
					className={ "install-addon-redirect-button" }
					variant="primary"
					onClick={ goToSite }
				>
					<FormattedMessage{ ...messages.goToSite } />
					<ArrowTopRightOnSquareIcon className={ styles.externalLinkIcon } />
				</Button>
			</div> }

			<p>
				<em><FormattedMessage { ...messages.videoDescription } /></em>
			</p>

			<iframe
				title={ "Step-by-step installation guide" }
				className={ styles.videoFrame }
				width="500"
				height="250"
				src={ "https://fast.wistia.net/embed/iframe/wogh6vsr21?dnt=1&autoPlay=false" }
				allowFullScreen={ true }
				frameBorder="0"
			/>

			{ ( ! invoiceNumberParam || plugins.length > 1 ) && <p>
				<em className={ styles.otherPluginAlert }>
					<FormattedMessage { ...messages.note } tagName={ "strong" } />
					<FormattedMessage { ...messages.otherPluginDirections } />
				</em>
			</p> }

			{ showDownloadIframe && <iframe
				title="download"
				width="0"
				height="0"
				src={ premiumDownloadUrl }
				style={ { display: "none" } }
			/> }

			<hr />
			<StepNavigation />
		</div>
	);
};

Downloading.propTypes = {
	intl: intlShape.isRequired,
};

export default injectIntl( Downloading );
